import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { submitBook } from "@/lib/api";

const BOOK_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_golden-wolf-premium/artifacts/0ag6fthu_WhatsApp%20Image%202026-09-01%20at%2021.43.26.jpeg";

const books = [
  {
    id: "consciousness",
    name: "The Consciousness of Abundance",
    status: "Coming Soon — reserve your copy",
    desc: "Twelve chapters. One reset. A field guide for the man rebuilding his life on his own terms.",
    canReserve: true,
  },
  {
    id: "wolfkeeper-2",
    name: "Wolf Keeper 2.0",
    status: "Coming Soon",
    desc: "The next-generation manual on canine leadership, human behavior, and building relationships that hold under pressure.",
    canReserve: false,
  },
];

const speakingTopics = [
  "Leadership Through Animal Psychology",
  "The Consciousness of Abundance",
  "Building a Business Around Your Natural Gifts",
  "AI for Everyday Entrepreneurs",
  "From Chicago to Nairobi",
  "The Future of the Animal-Care Industry",
  "Veterans, Service Dogs and Purpose",
  "Culture, Community and Economic Power",
  "Reinvention After 50",
  "Turning Vision Into Execution",
];

export default function BooksMedia() {
  const [tier] = useState("standard");
  const [form, setForm] = useState({ name: "", email: "", phone: "", tier: "standard", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { document.title = "Books & Media — Toriano Sanzone"; window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await submitBook({ ...form, tier });
      toast.success("Reserved. You're on the list — we'll notify you when Consciousness of Abundance ships.");
      setForm({ name: "", email: "", phone: "", tier, message: "" });
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-[120px] md:pt-[140px]" data-testid="books-media-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <div className="text-eyebrow text-gold mb-4">Books & Media</div>
        <h1 className="font-display text-[clamp(2.4rem,7vw,7rem)] leading-[0.92] tracking-[-0.03em] max-w-5xl">
          Ideas that <span className="italic gold-foil">move people.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-bone/75 font-editorial text-lg">
          Books, interviews, podcasts and long-form thinking from the pen of the Wolfkeeper — Chicago streets, Nairobi mountains, everything in between.
        </p>
      </section>

      {/* Books */}
      <section className="section pt-0">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} className="md:col-span-5">
              <div className="relative aspect-[3/4] overflow-hidden spotlight">
                <img src={BOOK_IMG} alt="Toriano Sanzone — author portrait" className="w-full h-full object-cover object-[center_20%]"/>
                <div className="absolute inset-0 border border-gold/40"/>
                <div className="absolute bottom-6 left-6 right-6 z-10 border-t border-gold/40 pt-4">
                  <div className="text-hair text-gold">A book by</div>
                  <div className="font-display text-3xl italic text-bone">Toriano Sanzone</div>
                </div>
              </div>
            </motion.div>
            <div className="md:col-span-7 space-y-8">
              {books.map((b, i) => (
                <motion.div key={b.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 * i }}
                  className="border-b border-hair pb-6"
                  data-testid={`book-${b.id}`}
                >
                  <div className="text-hair text-gold mb-2">{b.status}</div>
                  <h3 className="font-display text-3xl md:text-4xl italic">{b.name}</h3>
                  <p className="mt-3 text-bone/75 font-editorial">{b.desc}</p>
                  {b.canReserve && (
                    <a href="#reserve" className="mt-4 inline-flex items-center gap-2 text-hair text-gold hover:underline">Reserve your copy →</a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reservation form */}
      <section id="reserve" className="section pt-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <form onSubmit={submit} className="border-t border-hair pt-16 grid md:grid-cols-12 gap-8" data-testid="book-form">
            <div className="md:col-span-5">
              <div className="text-eyebrow text-gold">Reserve · Consciousness of Abundance</div>
              <h3 className="font-display text-4xl md:text-5xl mt-4">Claim your <span className="italic gold-foil">edition.</span></h3>
              <p className="mt-6 text-bone/70 font-editorial">First 100 reservations receive a hand-signed foil bookplate. We&apos;ll email you when the book is available for purchase.</p>
            </div>
            <div className="md:col-span-7 grid md:grid-cols-2 gap-6">
              <div className="field"><label>Name</label><input required value={form.name} onChange={upd("name")} data-testid="book-name"/></div>
              <div className="field"><label>Email</label><input required type="email" value={form.email} onChange={upd("email")} data-testid="book-email"/></div>
              <div className="field md:col-span-2"><label>Phone (optional)</label><input value={form.phone} onChange={upd("phone")} data-testid="book-phone"/></div>
              <div className="field md:col-span-2"><label>Message</label><textarea value={form.message} onChange={upd("message")} data-testid="book-message"/></div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} className="btn-gold" data-testid="book-submit">
                  {loading ? "Reserving…" : "Reserve My Copy"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Speaking */}
      <section className="section bg-ink-2">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-10">
            <div className="md:col-span-4">
              <div className="text-eyebrow text-gold">Speaking</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-[clamp(2.2rem,5.5vw,5.5rem)] leading-[0.95] tracking-[-0.02em]">
                Ideas that <span className="italic gold-foil">move people.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-2 mb-10">
            {speakingTopics.map((t, i) => (
              <motion.div key={t}
                initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.03 * i }}
                className="flex items-center gap-4 border-b border-hair pb-3"
              >
                <span className="text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-bone/85">{t}</span>
              </motion.div>
            ))}
          </div>
          <a href="/work-with-toriano?type=event" className="btn-gold" data-testid="books-speaking-cta">Book Toriano to Speak</a>
        </div>
      </section>
    </div>
  );
}
